import { createFileRoute } from '@tanstack/react-router';
import { useMemo, useState, type CSSProperties } from 'react';
import {
  ActionIcon, Button, Center, Drawer, Group, Loader, ScrollArea, SegmentedControl, Select, SimpleGrid,
  Stack, Table, Text, TextInput, Textarea, Tooltip, rem,
} from '@mantine/core';
import { QueryErrorAlert } from '@/shared/components/QueryErrorAlert';
import { useForm } from '@mantine/form';
import { useDisclosure } from '@mantine/hooks';
import {
  IconBolt, IconCheck, IconEye, IconFileCertificate, IconFileText, IconLanguage, IconPencil, IconPlus,
  IconRefresh, IconSearch, IconVersions, IconX,
} from '@tabler/icons-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { createOne, fetchAllPages, updateOne } from '@/shared/api/jsonapi/client';
import { httpRequest } from '@/shared/api/client';
import { notifySuccess, notifyError } from '@/shared/lib/notify';
import { MaeShell, MaePageHead } from '@/shared/desktop/MaeShell';
import { MobileHeader } from '@/shared/mobile/MobileHeader';
import { useIsMobileShell } from '@/shared/mobile/useIsMobileShell';
import { StatCard } from '@/shared/components/StatCard';
import { mw, MW_CARD_SHADOW, MW_CODE_STYLE, MW_MACHINE_FONT } from '@/shared/mobile/tokens';
import type { Denormalized } from '@/shared/api/jsonapi/parser';
import { Paginated } from '@/shared/components/DataTable/TablePagination';

interface LegalTerm extends Denormalized {
  type: 'legal-terms';
  code?: string;
  title?: string;
  note?: string;
  language?: string;
  version?: string;
  active?: boolean;
  content?: string;
  dataActivation?: string | null;
}

export const Route = createFileRoute('/_app/legals')({ component: LegalsPage });

type StatusFilter = 'all' | 'active' | 'draft';

interface FormVals { code: string; title: string; language: string; version: string; note: string; content: string }
const EMPTY: FormVals = { code: '', title: '', language: 'it-IT', version: '1.0', note: '', content: '' };

const LANGS = ['it-IT', 'en-US', 'fr-FR', 'de-DE', 'es-ES'].map((v) => ({ value: v, label: v }));

const CARD: CSSProperties = {
  border: `1px solid ${mw.line}`, borderRadius: 16, background: mw.surface, boxShadow: MW_CARD_SHADOW,
};
const TH: CSSProperties = {
  background: mw.surface2, color: mw.ink300, fontSize: 'var(--fs-compact)', fontWeight: 700,
  letterSpacing: '0.06em', textTransform: 'uppercase', borderBottom: `1px solid ${mw.line}`, whiteSpace: 'nowrap',
};
const chip = (label: string, bg: string, color: string, code = false) => (
  <span style={{ display: 'inline-block', fontSize: code ? 'var(--fs-compact)' : 'var(--fs-compact)', fontWeight: 600, padding: '3px 9px', borderRadius: 999, background: bg, color, ...(code ? MW_CODE_STYLE : null), whiteSpace: 'nowrap' }}>{label}</span>
);

function LegalsPage() {
  const qc = useQueryClient();
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<StatusFilter>('all');
  const [drawerOpen, drawer] = useDisclosure(false);
  const [editing, setEditing] = useState<LegalTerm | null>(null);
  const [preview, setPreview] = useState(false);
  const isMobile = useIsMobileShell();

  const listQ = useQuery({
    queryKey: ['legal-terms-all'],
    queryFn: () => fetchAllPages<LegalTerm>('legal-terms', { sort: ['code', '-version'] }, 200),
  });
  const all = useMemo(() => listQ.data ?? [], [listQ.data]);

  const stats = useMemo(() => {
    const active = all.filter((r) => r.active).length;
    const langs = new Set(all.map((r) => r.language).filter(Boolean)).size;
    return { total: all.length, active, draft: all.length - active, langs };
  }, [all]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return all.filter((r) => {
      if (status === 'active' && !r.active) return false;
      if (status === 'draft' && r.active) return false;
      if (q && !(`${r.code ?? ''} ${r.title ?? ''} ${r.language ?? ''}`.toLowerCase().includes(q))) return false;
      return true;
    });
  }, [all, status, search]);

  const form = useForm<FormVals>({
    mode: 'controlled',
    initialValues: EMPTY,
    validate: {
      code: (v) => (v.trim() ? null : 'Codice obbligatorio'),
      title: (v) => (v.trim() ? null : 'Titolo obbligatorio'),
    },
  });

  const openCreate = () => { setEditing(null); setPreview(false); form.setValues(EMPTY); form.resetDirty(EMPTY); drawer.open(); };
  const openEdit = (r: LegalTerm) => {
    setEditing(r); setPreview(false);
    const v: FormVals = {
      code: r.code ?? '', title: r.title ?? '', language: r.language ?? 'it-IT',
      version: r.version ?? '1.0', note: r.note ?? '', content: r.content ?? '',
    };
    form.setValues(v); form.resetDirty(v); drawer.open();
  };

  const save = useMutation({
    mutationFn: (v: FormVals) => {
      const attributes = { code: v.code, title: v.title, language: v.language, version: v.version, note: v.note, content: v.content };
      return editing?.id
        ? updateOne<LegalTerm>('legal-terms', { id: editing.id, attributes })
        : createOne<LegalTerm>('legal-terms', { attributes });
    },
    onSuccess: () => {
      notifySuccess(editing ? 'Documento aggiornato' : 'Documento creato');
      qc.invalidateQueries({ queryKey: ['legal-terms-all'] });
      drawer.close();
    },
    onError: (e) => notifyError(e, 'Salvataggio fallito'),
  });

  // Attivazione: endpoint custom che rende attiva SOLO questa versione per (code, language),
  // disattivando le altre. NON usiamo un PATCH diretto su `active` per non violare la regola.
  const activate = useMutation({
    mutationFn: (id: string) => httpRequest('/legal-terms-custom/activation', { method: 'PATCH', endpointType: 'plain', body: { id } }),
    onSuccess: () => {
      notifySuccess('Documento attivato');
      qc.invalidateQueries({ queryKey: ['legal-terms-all'] });
      drawer.close();
    },
    onError: (e) => notifyError(e, 'Attivazione fallita'),
  });

  const headActions = (
    <>
      <Tooltip label="Aggiorna">
        <ActionIcon variant="light" color="dark" size="lg" aria-label="Aggiorna" onClick={() => qc.invalidateQueries({ queryKey: ['legal-terms-all'] })}>
          <IconRefresh size={18} />
        </ActionIcon>
      </Tooltip>
      <Button leftSection={<IconPlus size={16} />} style={{ background: mw.brandFill }} onClick={openCreate}>
        Nuovo documento
      </Button>
    </>
  );

  const kpi = (
    <SimpleGrid cols={{ base: 2, sm: 2, lg: 4 }} spacing={{ base: 12, sm: 14 }} mb={18}>
      <StatCard label="Documenti" value={stats.total} icon={IconFileCertificate} color="brand" loading={listQ.isLoading} />
      <StatCard label="Attivi" value={stats.active} icon={IconCheck} color="green" loading={listQ.isLoading} />
      <StatCard label="Bozze" value={stats.draft} icon={IconFileText} color="gray" loading={listQ.isLoading} />
      <StatCard label="Lingue" value={stats.langs} icon={IconLanguage} color="blue" loading={listQ.isLoading} />
    </SimpleGrid>
  );

  const drawerEl = (
    <Drawer
      opened={drawerOpen}
      onClose={drawer.close}
      position="right"
      size="lg"
      title={
        <Group gap={10}>
          <span style={{ width: 34, height: 34, borderRadius: 10, background: mw.brandTint, color: mw.brand, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><IconFileCertificate size={18} /></span>
          <div>
            <Text style={{ fontSize: 'var(--fs-body-lg)', fontWeight: 700, color: mw.ink900 }}>{editing ? 'Modifica documento' : 'Nuovo documento'}</Text>
            {editing && <Text style={{ fontSize: 'var(--fs-compact)', color: mw.ink500 }}>{editing.active ? 'Versione attiva' : 'Bozza'}</Text>}
          </div>
        </Group>
      }
    >
      <form onSubmit={form.onSubmit((v) => save.mutate(v))}>
        <Stack gap="sm">
          <TextInput label="Titolo" required {...form.getInputProps('title')} />
          <Group grow>
            <TextInput label="Codice" required placeholder="privacy-policy" {...form.getInputProps('code')} />
            <Select label="Lingua" data={LANGS} searchable {...form.getInputProps('language')} />
          </Group>
          <Group grow>
            <TextInput label="Versione" leftSection={<IconVersions size={14} />} placeholder="1.0" {...form.getInputProps('version')} />
          </Group>
          <Textarea label="Note interne" autosize minRows={2} {...form.getInputProps('note')} />

          <Group justify="space-between" align="center" mt={4}>
            <Text style={{ fontSize: 'var(--fs-compact)', fontWeight: 600, color: mw.ink900 }}>Contenuto HTML</Text>
            <Button size="xs" variant="light" color="dark" leftSection={preview ? <IconPencil size={13} /> : <IconEye size={13} />}
              onClick={() => setPreview((p) => !p)} disabled={!form.values.content}>
              {preview ? 'Modifica' : 'Anteprima'}
            </Button>
          </Group>
          {preview ? (
            <div style={{ ...CARD, boxShadow: 'none', padding: 16, maxHeight: 320, overflow: 'auto' }}>
              <div style={{ fontSize: 'var(--fs-body)', color: mw.ink900, lineHeight: 1.6 }} dangerouslySetInnerHTML={{ __html: form.values.content }} />
            </div>
          ) : (
            <Textarea autosize minRows={8} maxRows={20} placeholder="<h1>…</h1>" styles={{ input: { fontFamily: MW_MACHINE_FONT, fontSize: 'var(--fs-compact)' } }} {...form.getInputProps('content')} />
          )}

          <Group justify="space-between" mt="md">
            <div>
              {editing && !editing.active && (
                <Button variant="light" color="teal" leftSection={<IconBolt size={15} />} loading={activate.isPending}
                  onClick={() => editing.id && activate.mutate(editing.id)}>
                  Attiva questa versione
                </Button>
              )}
            </div>
            <Group gap="xs">
              <Button variant="subtle" color="dark" onClick={drawer.close}>Annulla</Button>
              <Button type="submit" loading={save.isPending} style={{ background: mw.brandFill }}>Salva</Button>
            </Group>
          </Group>
        </Stack>
      </form>
    </Drawer>
  );

  // ─── Mobile: header nativo + KPI + chip stato + ricerca + lista di card ───
  if (isMobile) {
    return (
      <Stack gap={16} px={20} pt={16} pb={24}>
        <MobileHeader title="Legali" />
        <Group justify="flex-end">{headActions}</Group>
        {kpi}

        <div style={{ display: 'flex', gap: 8, overflowX: 'auto', margin: '0 -20px', padding: '0 20px 2px' }}>
          {([['all', `Tutti · ${stats.total}`], ['active', `Attivi · ${stats.active}`], ['draft', `Bozze · ${stats.draft}`]] as const).map(([v, label]) => {
            const on = status === v;
            return (
              <button key={v} onClick={() => setStatus(v)} className="mw-pressable"
                style={{ flexShrink: 0, height: rem(34), padding: '0 14px', borderRadius: 999, cursor: 'pointer', fontSize: 'var(--fs-compact)', fontWeight: 600, whiteSpace: 'nowrap', fontFamily: 'inherit', border: on ? `1px solid ${mw.brand}` : `1px solid ${mw.line}`, background: on ? mw.brandFill : mw.surface, color: on ? 'var(--on-brand)' : mw.ink500 }}>
                {label}
              </button>
            );
          })}
        </div>

        <TextInput
          placeholder="Cerca codice, titolo, lingua…"
          leftSection={<IconSearch size={16} />}
          rightSection={search ? <ActionIcon variant="subtle" size="sm" aria-label="Pulisci" onClick={() => setSearch('')}><IconX size={12} /></ActionIcon> : null}
          value={search}
          onChange={(e) => setSearch(e.currentTarget.value)}
          radius="md"
        />

        {listQ.isError ? <QueryErrorAlert error={listQ.error} onRetry={() => { void listQ.refetch(); }} /> : listQ.isLoading ? (
          <Stack gap={10}>{Array.from({ length: 4 }).map((_, i) => <div key={i} style={{ height: 76, borderRadius: 14, background: mw.surface2 }} />)}</Stack>
        ) : filtered.length === 0 ? (
          <div style={{ ...CARD, padding: '28px 18px', textAlign: 'center' }}>
            <IconFileCertificate size={28} color={mw.ink300} />
            <Text style={{ fontSize: 'var(--fs-body-lg)', fontWeight: 700, color: mw.ink900, marginTop: 8 }}>{all.length === 0 ? 'Nessun documento' : 'Nessun risultato'}</Text>
            <Text style={{ fontSize: 'var(--fs-compact)', color: mw.ink500, marginTop: 2 }}>{all.length === 0 ? 'Crea il primo con «Nuovo documento».' : 'Cambia filtro o ricerca.'}</Text>
          </div>
        ) : (
          <Stack gap={10}>
            {filtered.map((r) => (
              <button key={r.id} onClick={() => openEdit(r)} className="mw-pressable"
                style={{ textAlign: 'left', cursor: 'pointer', width: '100%', ...CARD, borderRadius: 14, padding: 14 }}>
                <Group justify="space-between" wrap="nowrap" mb={6}>
                  <Text size="sm" fw={700} style={{ color: mw.ink900 }} lineClamp={1}>{r.title ?? '—'}</Text>
                  {r.active ? chip('● Attivo', mw.successBg, mw.success) : chip('Bozza', mw.surface2, mw.ink500)}
                </Group>
                <Group gap={6} wrap="wrap" align="center">
                  {r.code && chip(r.code, mw.surface2, mw.ink900, true)}
                  {r.language && chip(r.language, mw.accentTint, mw.accentText)}
                  <Text size="xs" c="dimmed" style={{ fontVariantNumeric: 'tabular-nums' }}>v{r.version ?? '—'}</Text>
                  <div style={{ flex: 1 }} />
                  <Text size="xs" c="dimmed" style={{ fontVariantNumeric: 'tabular-nums' }}>{r.dataActivation ? dayjs(r.dataActivation).format('DD/MM/YY') : '—'}</Text>
                </Group>
              </button>
            ))}
          </Stack>
        )}

        {drawerEl}
      </Stack>
    );
  }

  return (
    <MaeShell>
      <MaePageHead
        icon={<IconFileCertificate size={22} />}
        title="Termini & Condizioni"
        sub="Documenti legali dell’azienda: privacy policy, T&C, cookie policy — versionati per lingua."
        tone="bordeaux"
        actions={headActions}
      />

      {kpi}

      <div style={{ ...CARD, padding: '14px 16px', marginBottom: 16 }}>
        <Group wrap="wrap" gap="sm">
          <SegmentedControl
            value={status}
            onChange={(v) => setStatus(v as StatusFilter)}
            data={[
              { value: 'all', label: `Tutti · ${stats.total}` },
              { value: 'active', label: `Attivi · ${stats.active}` },
              { value: 'draft', label: `Bozze · ${stats.draft}` },
            ]}
          />
          <TextInput
            placeholder="Cerca codice, titolo o lingua…"
            leftSection={<IconSearch size={14} />}
            rightSection={search ? (
              <ActionIcon variant="subtle" size="sm" aria-label="Pulisci" onClick={() => setSearch('')}><IconX size={12} /></ActionIcon>
            ) : null}
            value={search}
            onChange={(e) => setSearch(e.currentTarget.value)}
            style={{ marginLeft: 'auto', minWidth: 280, flex: 1 }}
          />
        </Group>
      </div>

      {listQ.isError ? <QueryErrorAlert error={listQ.error} onRetry={() => { void listQ.refetch(); }} /> : listQ.isLoading ? (
        <Center py={60}><Loader /></Center>
      ) : filtered.length === 0 ? (
        <div style={{ ...CARD, padding: '36px 20px', textAlign: 'center' }}>
          <span style={{ display: 'inline-flex', width: 52, height: 52, borderRadius: 14, background: mw.brandTint, color: mw.brand, alignItems: 'center', justifyContent: 'center', marginBottom: 12 }}>
            <IconFileCertificate size={26} />
          </span>
          <Text style={{ fontSize: 'var(--fs-body-lg)', fontWeight: 700, color: mw.ink900 }}>
            {all.length === 0 ? 'Nessun documento legale' : 'Nessun risultato per i filtri correnti'}
          </Text>
          <Text style={{ fontSize: 'var(--fs-compact)', color: mw.ink500, marginTop: 4 }}>
            {all.length === 0 ? 'Crea il primo documento con «Nuovo documento».' : 'Prova a cambiare filtro o ricerca.'}
          </Text>
        </div>
      ) : (
        <div style={{ ...CARD, overflow: 'hidden' }}>
          <Paginated rows={filtered} label="documenti">
          {(pageRows) => (
          <ScrollArea type="auto">
            <Table verticalSpacing={12} horizontalSpacing="md" highlightOnHover>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th style={{ ...TH, minWidth: 200 }}>Codice</Table.Th>
                  <Table.Th style={TH}>Titolo</Table.Th>
                  <Table.Th style={{ ...TH, minWidth: 90 }}>Lingua</Table.Th>
                  <Table.Th style={{ ...TH, minWidth: 90 }}>Versione</Table.Th>
                  <Table.Th style={{ ...TH, minWidth: 130 }}>Stato</Table.Th>
                  <Table.Th style={{ ...TH, minWidth: 120 }}>Attivazione</Table.Th>
                  <Table.Th style={{ ...TH, width: 60 }} />
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {pageRows.map((r) => (
                  <Table.Tr key={r.id} style={{ cursor: 'pointer' }} onClick={() => openEdit(r)}>
                    <Table.Td>{r.code ? chip(r.code, mw.surface2, mw.ink900, true) : <Text style={{ fontSize: 'var(--fs-compact)', color: mw.ink300 }}>—</Text>}</Table.Td>
                    <Table.Td><Text style={{ fontSize: 'var(--fs-compact)', fontWeight: 600, color: mw.ink900 }}>{r.title ?? '—'}</Text></Table.Td>
                    <Table.Td>{r.language ? chip(r.language, mw.accentTint, mw.accentText) : <Text style={{ fontSize: 'var(--fs-compact)', color: mw.ink300 }}>—</Text>}</Table.Td>
                    <Table.Td><Text style={{ fontSize: 'var(--fs-compact)', color: mw.ink500, fontVariantNumeric: 'tabular-nums' }}>{r.version ?? '—'}</Text></Table.Td>
                    <Table.Td>{r.active ? chip('● Attivo', mw.successBg, mw.success) : chip('Bozza', mw.surface2, mw.ink500)}</Table.Td>
                    <Table.Td><Text style={{ fontSize: 'var(--fs-compact)', color: mw.ink500 }}>{r.dataActivation ? dayjs(r.dataActivation).format('DD/MM/YYYY') : '—'}</Text></Table.Td>
                    <Table.Td><ActionIcon variant="subtle" color="gray" aria-label="Modifica"><IconPencil size={15} /></ActionIcon></Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </ScrollArea>
          )}
          </Paginated>
        </div>
      )}

      {drawerEl}
    </MaeShell>
  );
}
